import { createTheme, Theme } from '@mui/material/styles';

// Cor base (primary/secondary) de cada módulo. Os demais tokens (tipografia,
// espaçamento, componentes) são compartilhados por todos os módulos.
type ModuleColorSet = {
  primary: { main: string; light: string; dark: string; contrastText: string };
  secondary: { main: string; light: string; dark: string; contrastText: string };
};

const modulePalettes: Record<number, ModuleColorSet> = {
  // 1 - SDP (Sistema de Diagramação e Publicação) - roxo
  1: {
    primary: { main: '#7B1FA2', light: '#BA68C8', dark: '#4A148C', contrastText: '#ffffff' },
    secondary: { main: '#9C27B0', light: '#CE93D8', dark: '#6A0080', contrastText: '#ffffff' },
  },
  // 2 - SER (Sistema de Envio de Remessas) - índigo
  2: {
    primary: { main: '#3949AB', light: '#6F74DD', dark: '#00227B', contrastText: '#ffffff' },
    secondary: { main: '#5C6BC0', light: '#8E99F3', dark: '#26418F', contrastText: '#ffffff' },
  },
  // 4 - SIC (Serviço de Informação ao Cidadão) - amarelo-mostarda (verde já é do SGE)
  4: {
    primary: { main: '#A67C00', light: '#D9AB3A', dark: '#735000', contrastText: '#ffffff' },
    secondary: { main: '#C49000', light: '#F9C046', dark: '#8E6300', contrastText: '#ffffff' },
  },
  // 7 - OUV (Ouvidoria) - laranja-escuro
  7: {
    primary: { main: '#D84315', light: '#FF7543', dark: '#9F0000', contrastText: '#ffffff' },
    secondary: { main: '#F4511E', light: '#FF844C', dark: '#B91400', contrastText: '#ffffff' },
  },
  // 9 - SCP (Sistema de Contratações Públicas) - azul
  9: {
    primary: { main: '#2196f3', light: '#64b5f6', dark: '#1976d2', contrastText: '#ffffff' },
    secondary: { main: '#03a9f4', light: '#4fc3f7', dark: '#0288d1', contrastText: '#ffffff' },
  },
  // 10 - SGE (Sistema de Emendas Parlamentares) - verde
  10: {
    primary: { main: '#2E7D32', light: '#60ad5e', dark: '#005005', contrastText: '#ffffff' },
    secondary: { main: '#43A047', light: '#76d275', dark: '#00701a', contrastText: '#ffffff' },
  },
  // 8 - GED (Documentos Eletrônicos) - cinza-azulado
  8: {
    primary: { main: '#455A64', light: '#718792', dark: '#1C313A', contrastText: '#ffffff' },
    secondary: { main: '#607D8B', light: '#8EACBB', dark: '#34515E', contrastText: '#ffffff' },
  },
  // 11 - ADM (Administração da Empresa) - vinho
  11: {
    primary: { main: '#8E244D', light: '#C2537A', dark: '#5C0026', contrastText: '#ffffff' },
    secondary: { main: '#AD3A63', light: '#E26A90', dark: '#78003A', contrastText: '#ffffff' },
  },
  // 3 - GPE/APM (Portal da Entidade) - verde-azulado
  3: {
    primary: { main: '#00897B', light: '#4DB6AC', dark: '#00695C', contrastText: '#ffffff' },
    secondary: { main: '#26A69A', light: '#80CBC4', dark: '#00796B', contrastText: '#ffffff' },
  },
};

const defaultModuleId = 9;

export const createAppTheme = (moduleId?: number): Theme => {
  const colors = modulePalettes[moduleId ?? defaultModuleId] ?? modulePalettes[defaultModuleId];

  return createTheme({
    palette: {
      primary: colors.primary,
      secondary: colors.secondary,
      error: {
        main: '#f44336',
        light: '#e57373',
        dark: '#d32f2f',
      },
      warning: {
        main: '#ff9800',
        light: '#ffb74d',
        dark: '#f57c00',
      },
      // Azul informativo fixo: igual à cor primária, avisos e chips "info" não se distinguiam do tema do módulo
      info: {
        main: '#0288d1',
        light: '#03a9f4',
        dark: '#01579b',
      },
      success: {
        main: '#4caf50',
        light: '#81c784',
        dark: '#388e3c',
      },
      background: {
        default: '#fafafa',
        paper: '#ffffff',
      },
      text: {
        primary: '#212121',
        secondary: '#757575',
        disabled: '#bdbdbd',
      },
      divider: '#e0e0e0',
      action: {
        hover: `${colors.primary.main}14`,
        selected: `${colors.primary.main}24`,
        disabled: '#bdbdbd',
        disabledBackground: '#e0e0e0',
      },
    },
    typography: {
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(','),
      h1: {
        fontSize: '2rem',
        fontWeight: 700,
        letterSpacing: '-0.02em',
        lineHeight: 1.2,
      },
      h2: {
        fontSize: '1.75rem',
        fontWeight: 700,
        letterSpacing: '-0.01em',
        lineHeight: 1.25,
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        lineHeight: 1.3,
      },
      h4: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
      },
      button: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.9375rem',
      },
    },
    shape: {
      borderRadius: 8,
    },
    shadows: [
      'none',
      '0 1px 2px 0 rgb(0 0 0 / 0.05)',
      '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
      '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
      '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
      '0 25px 50px -12px rgb(0 0 0 / 0.25)',
      '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
      '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
      '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
      '0 25px 50px -12px rgb(0 0 0 / 0.25)',
      '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
      '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
      '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
      '0 25px 50px -12px rgb(0 0 0 / 0.25)',
      '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
      '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
      '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
      '0 25px 50px -12px rgb(0 0 0 / 0.25)',
      '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
      '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    ],
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '10px 20px',
            fontSize: '0.9375rem',
            fontWeight: 500,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
            },
          },
          contained: {
            '&:hover': {
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
            },
          },
          outlined: {
            borderWidth: '1.5px',
            '&:hover': {
              borderWidth: '1.5px',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
          elevation1: {
            boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          },
          elevation2: {
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
          },
          elevation3: {
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              '& fieldset': {
                borderColor: '#e0e0e0',
              },
              '&:hover fieldset': {
                borderColor: '#bdbdbd',
              },
              '&.Mui-focused fieldset': {
                borderWidth: '2px',
                borderColor: theme.palette.primary.main,
              },
            },
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            borderRadius: 8,
            '& fieldset': {
              borderColor: '#e0e0e0',
            },
            '&:hover fieldset': {
              borderColor: '#bdbdbd',
            },
            '&.Mui-focused fieldset': {
              borderWidth: '2px',
              borderColor: theme.palette.primary.main,
            },
          }),
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 500,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            borderBottom: '1px solid #e0e0e0',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: '1px solid #e0e0e0',
            boxShadow: 'none',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: '#e0e0e0',
          },
          head: ({ theme }: { theme: Theme }) => ({
            fontWeight: 600,
            backgroundColor: '#f5f5f5',
            color: theme.palette.primary.main,
          }),
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: '#e0e0e0',
          },
        },
      },
    },
  });
};

// Tema padrão (módulo SCP) para consumidores que não dependem do módulo atual.
const theme = createAppTheme(defaultModuleId);

export default theme;
